import React, { useEffect, useState, useRef } from "react";
import api, { formatApiError } from "../api";
import {
  Upload, FileText, Trash2, Lock, Download, ShieldCheck, Camera, ArrowLeft,
  Star, Check, Circle, ChevronRight, AlertTriangle, Inbox, Users, Home, TrendingUp,
  AlertCircle, Shield, Feather, Cloud, X,
} from "lucide-react";
import { COFFRE_FOLDERS, FOLDER_BY_ID, subcategoryLabel } from "../coffreFolders";

const ICONS = { Users, Home, TrendingUp, AlertCircle, Shield, FileText, Feather, Cloud, Inbox };

export default function Coffre() {
  const [plan, setPlan] = useState(null);
  const [summary, setSummary] = useState(null);
  const [docs, setDocs] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [error, setError] = useState("");

  const loadAll = async () => {
    const [{ data: carnet }, { data: sum }] = await Promise.all([
      api.get("/carnet"),
      api.get("/coffre/folders"),
    ]);
    setPlan(carnet.plan);
    setDocs(carnet.documents || []);
    setSummary(sum);
  };
  useEffect(() => { loadAll(); }, []);

  if (!plan) return <div>Chargement…</div>;

  const locked = plan.id === "decouverte";

  if (locked) {
    return (
      <div className="space-y-8">
        <div>
          <div style={{ color: "var(--auprea-gold)", fontSize: 12, letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 600, marginBottom: 12 }}>
            — Sécurisé, accès restreint à vous seul
          </div>
          <h1 style={{ fontSize: "clamp(2rem, 4vw, 2.75rem)" }} data-testid="coffre-title">Coffre-fort</h1>
        </div>
        <div className="auprea-card" style={{ borderColor: "var(--auprea-gold)", backgroundColor: "#faf4e8" }}>
          <Lock size={28} color="#caa86a" />
          <h3 style={{ fontSize: 22, margin: "12px 0 8px" }}>Coffre-fort réservé aux abonnés</h3>
          <p>Passez à un forfait Essentiel, Confort ou Patrimoine+ pour stocker vos documents.</p>
          <a href="/tarifs"><button className="auprea-btn-gold mt-4" data-testid="btn-coffre-upgrade">Voir les forfaits</button></a>
        </div>
      </div>
    );
  }

  if (selectedFolder) {
    return (
      <FolderDetailView
        folder={selectedFolder}
        docs={docs.filter((d) => (d.folder || "non_classes") === selectedFolder.id)}
        onBack={() => setSelectedFolder(null)}
        onReload={loadAll}
        onOpenUpload={() => setUploadOpen(true)}
        uploadOpen={uploadOpen}
        closeUpload={() => setUploadOpen(false)}
        setError={setError}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <div style={{ color: "var(--auprea-gold)", fontSize: 12, letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 600, marginBottom: 12 }}>
          — Sécurisé, accès restreint à vous seul
        </div>
        <h1 style={{ fontSize: "clamp(2rem, 4vw, 2.75rem)" }} data-testid="coffre-title">Coffre-fort</h1>
        <p style={{ fontSize: 17, color: "rgba(35,38,46,0.7)", marginTop: 8 }}>
          Vos documents importants classés par thématique. Cliquez sur un dossier pour ajouter ou consulter vos pièces.
        </p>
      </div>

      <div
        className="auprea-card"
        data-testid="security-banner"
        style={{ borderLeft: "3px solid var(--auprea-green)", background: "rgba(47,125,91,0.05)", padding: "14px 18px", display: "flex", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}
      >
        <ShieldCheck size={22} color="#2f7d5b" style={{ flexShrink: 0, marginTop: 2 }} />
        <div style={{ flex: 1, minWidth: 240, fontSize: 14, lineHeight: 1.55 }}>
          <strong style={{ color: "var(--auprea-navy)" }}>Chiffrement + hébergement France &amp; UE.</strong>{" "}
          <span style={{ color: "rgba(35,38,46,0.75)" }}>
            Vos documents sont chiffrés (AES-256) et hébergés exclusivement dans l&apos;Union européenne. Aucune URL publique — chaque téléchargement exige votre authentification.
          </span>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-3 gap-3" data-testid="coffre-kpi">
          <MiniStat label="Documents" value={summary.total_documents} testid="kpi-total" />
          <MiniStat label="Essentiels" value={summary.essential_count} testid="kpi-essential" icon={<Star size={13} fill="#caa86a" color="#caa86a" />} />
          <MiniStat label="Dossiers actifs" value={summary.folders.filter((f) => f.doc_count > 0).length} testid="kpi-folders" />
        </div>
      )}

      <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))" }} data-testid="folders-grid">
        {(summary?.folders || COFFRE_FOLDERS).map((f) => {
          const Icon = ICONS[f.icon] || FileText;
          const isSpecial = f.special;
          const docCount = f.doc_count ?? 0;
          const progression = f.progression ?? 0;
          const totalSub = f.total_subcategories ?? (f.subcategories || []).length;
          const filled = f.filled_subcategories ?? 0;
          return (
            <button
              key={f.id}
              onClick={() => setSelectedFolder(f)}
              className="auprea-card"
              data-testid={`folder-card-${f.id}`}
              style={{
                textAlign: "left", padding: 18,
                display: "flex", flexDirection: "column", gap: 10, cursor: "pointer",
                background: isSpecial ? "rgba(163,59,26,0.04)" : "#fff",
                borderLeft: isSpecial ? "3px solid #a33b1a" : `3px solid ${docCount > 0 ? "var(--auprea-gold)" : "rgba(21,41,79,0.12)"}`,
                transition: "transform 0.15s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                <div style={{ width: 40, height: 40, borderRadius: 4, background: isSpecial ? "rgba(163,59,26,0.1)" : "rgba(202,168,106,0.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Icon size={20} color={isSpecial ? "#a33b1a" : "#caa86a"} />
                </div>
                <ChevronRight size={18} color="rgba(35,38,46,0.35)" />
              </div>
              <div>
                <div style={{ fontFamily: "Spectral, serif", fontSize: 17, color: "var(--auprea-navy)", fontWeight: 500, lineHeight: 1.25 }}>{f.label}</div>
                {f.warning && (
                  <div style={{ fontSize: 11, color: "#a33b1a", marginTop: 4, display: "flex", alignItems: "center", gap: 4 }}>
                    <AlertTriangle size={11} /> Sensible
                  </div>
                )}
              </div>
              <div style={{ fontSize: 12, color: "rgba(35,38,46,0.65)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                <span data-testid={`folder-count-${f.id}`}>{docCount} document{docCount > 1 ? "s" : ""}</span>
                {!isSpecial && <span style={{ fontSize: 11, color: "rgba(35,38,46,0.5)" }}>{filled}/{totalSub} catégories</span>}
              </div>
              {!isSpecial && (
                <div style={{ height: 4, background: "rgba(21,41,79,0.08)", borderRadius: 2, overflow: "hidden" }}>
                  <div style={{ width: `${progression}%`, height: "100%", background: "var(--auprea-gold)", transition: "width 0.4s ease" }} data-testid={`folder-progress-${f.id}`} />
                </div>
              )}
            </button>
          );
        })}
      </div>

      {error && <div style={{ color: "#b53a3a", marginTop: 8, fontSize: 14 }} data-testid="coffre-error">{error}</div>}
    </div>
  );
}

function FolderDetailView({ folder, docs, onBack, onReload, onOpenUpload, uploadOpen, closeUpload, setError }) {
  const [busyId, setBusyId] = useState(null);
  const [moveDoc, setMoveDoc] = useState(null);
  const Icon = ICONS[folder.icon] || FileText;

  const patchDoc = async (docId, updates) => {
    setBusyId(docId);
    try {
      await api.patch(`/coffre/${docId}`, updates);
      await onReload();
    } catch (e) { setError && setError(formatApiError(e)); }
    finally { setBusyId(null); }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer ce document ?")) return;
    setBusyId(id);
    try {
      await api.delete(`/coffre/${id}`);
      await onReload();
    } catch (e) { setError && setError(formatApiError(e)); }
    finally { setBusyId(null); }
  };

  const downloadDoc = async (doc) => {
    try {
      const resp = await api.get(`/coffre/${doc.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = doc.nom;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (e) { setError && setError(formatApiError(e)); }
  };

  const docsBySubcat = {};
  docs.forEach((d) => {
    const sc = d.subcategory || "autre";
    if (!docsBySubcat[sc]) docsBySubcat[sc] = [];
    docsBySubcat[sc].push(d);
  });

  return (
    <div className="space-y-5">
      <button onClick={onBack} data-testid="btn-back-to-folders" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--auprea-navy)", padding: "6px 0", background: "none" }}>
        <ArrowLeft size={15} /> Retour aux dossiers
      </button>

      <div className="flex items-start gap-4 flex-wrap">
        <div style={{ width: 54, height: 54, borderRadius: 4, background: folder.special ? "rgba(163,59,26,0.1)" : "rgba(202,168,106,0.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Icon size={28} color={folder.special ? "#a33b1a" : "#caa86a"} />
        </div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <h1 style={{ fontSize: "clamp(1.5rem, 3vw, 2rem)", margin: 0 }} data-testid="folder-detail-title">{folder.label}</h1>
          <p style={{ fontSize: 14, color: "rgba(35,38,46,0.65)", marginTop: 4 }}>
            {docs.length} document{docs.length > 1 ? "s" : ""} · {folder.subcategories.length} sous-catégorie{folder.subcategories.length > 1 ? "s" : ""}
          </p>
        </div>
        <button onClick={onOpenUpload} className="auprea-btn" data-testid="btn-open-upload" style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "12px 20px", fontSize: 15 }}>
          <Upload size={17} /> Ajouter un document
        </button>
      </div>

      {folder.warning && (
        <div data-testid="folder-warning" style={{ background: "rgba(163,59,26,0.06)", border: "1px solid rgba(163,59,26,0.3)", borderLeft: "3px solid #a33b1a", padding: "12px 16px", fontSize: 13.5, lineHeight: 1.55, color: "#5a1e10", display: "flex", gap: 10, alignItems: "flex-start" }}>
          <AlertTriangle size={18} color="#a33b1a" style={{ flexShrink: 0, marginTop: 2 }} />
          <span>{folder.warning}</span>
        </div>
      )}

      <div className="auprea-card">
        <h2 style={{ fontSize: 18, marginBottom: 4 }}>Documents attendus dans ce dossier</h2>
        <p style={{ fontSize: 13, color: "rgba(35,38,46,0.6)", marginBottom: 14 }}>
          Utilisez cette liste comme guide. Un document = un fichier.
        </p>
        <div className="space-y-2" data-testid="checklist">
          {folder.subcategories.map((sc) => {
            const found = (docsBySubcat[sc.id] || []).length;
            const done = found > 0;
            return (
              <div key={sc.id} data-testid={`checklist-${sc.id}`} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", background: done ? "rgba(47,125,91,0.05)" : "rgba(21,41,79,0.02)", borderLeft: `3px solid ${done ? "var(--auprea-green)" : "rgba(21,41,79,0.15)"}` }}>
                {done ? <Check size={17} color="#2f7d5b" style={{ flexShrink: 0 }} /> : <Circle size={16} color="rgba(21,41,79,0.4)" style={{ flexShrink: 0 }} />}
                <span style={{ flex: 1, fontSize: 14, color: done ? "var(--auprea-navy)" : "rgba(35,38,46,0.75)" }}>
                  {sc.label}
                  {sc.essential && (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 3, marginLeft: 8, padding: "1px 6px", background: "rgba(202,168,106,0.2)", color: "#8a6f2e", fontSize: 10, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase" }}>
                      <Star size={9} fill="#caa86a" color="#caa86a" /> Essentiel
                    </span>
                  )}
                </span>
                {done && <span style={{ fontSize: 12, color: "rgba(35,38,46,0.55)", flexShrink: 0 }}>{found} déposé{found > 1 ? "s" : ""}</span>}
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <h2 style={{ fontSize: 18, marginBottom: 12 }}>Documents déposés ({docs.length})</h2>
        {docs.length === 0 ? (
          <div className="auprea-card text-center" style={{ color: "rgba(35,38,46,0.6)", padding: 30 }} data-testid="folder-empty">
            <Inbox size={30} color="rgba(35,38,46,0.3)" style={{ margin: "0 auto 10px" }} />
            <div style={{ fontSize: 14 }}>Aucun document dans ce dossier pour l&apos;instant.</div>
            <button onClick={onOpenUpload} className="auprea-btn-gold" style={{ marginTop: 16, padding: "10px 20px", fontSize: 14 }} data-testid="btn-first-upload">
              Ajouter votre premier document
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {docs.map((d) => (
              <div key={d.id} className="auprea-card" style={{ padding: 16, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14, flexWrap: "wrap" }} data-testid={`doc-${d.id}`}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0, flex: 1 }}>
                  <FileText size={26} color="#caa86a" style={{ flexShrink: 0 }} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <button onClick={() => downloadDoc(d)} style={{ fontWeight: 500, color: "var(--auprea-navy)", textAlign: "left", background: "none", border: "none", padding: 0, cursor: "pointer", display: "block", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} data-testid={`doc-link-${d.id}`}>
                      {d.nom}
                    </button>
                    <div style={{ fontSize: 12, color: "rgba(35,38,46,0.6)", marginTop: 3 }}>
                      {subcategoryLabel(folder.id, d.subcategory)} · {new Date(d.ajoute_le).toLocaleDateString("fr-FR")} · {(d.taille / 1024).toFixed(0)} Ko
                    </div>
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
                  <button
                    onClick={() => patchDoc(d.id, { essential: !d.essential })}
                    disabled={busyId === d.id}
                    data-testid={`btn-essential-${d.id}`}
                    title={d.essential ? "Retirer du statut essentiel" : "Marquer comme essentiel"}
                    style={{ padding: "5px 10px", fontSize: 11, display: "inline-flex", alignItems: "center", gap: 4, background: d.essential ? "rgba(202,168,106,0.2)" : "transparent", color: d.essential ? "#8a6f2e" : "rgba(35,38,46,0.6)", border: `1px solid ${d.essential ? "var(--auprea-gold)" : "rgba(35,38,46,0.2)"}`, fontWeight: 600, letterSpacing: "0.03em", textTransform: "uppercase" }}
                  >
                    <Star size={12} fill={d.essential ? "#caa86a" : "none"} color={d.essential ? "#caa86a" : "currentColor"} />
                    {d.essential ? "Essentiel" : "Marquer essentiel"}
                  </button>
                  <button onClick={() => setMoveDoc(d)} data-testid={`btn-move-${d.id}`} style={{ fontSize: 12, padding: "5px 10px", color: "var(--auprea-navy)", background: "transparent", border: "1px solid rgba(21,41,79,0.2)" }}>Reclasser</button>
                  <button onClick={() => downloadDoc(d)} style={{ padding: 6, color: "var(--auprea-blue)" }} data-testid={`btn-download-doc-${d.id}`}>
                    <Download size={17} />
                  </button>
                  <button onClick={() => del(d.id)} style={{ padding: 6, color: "#b53a3a" }} data-testid={`btn-del-doc-${d.id}`}>
                    <Trash2 size={17} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {uploadOpen && (
        <UploadModal initialFolder={folder} onClose={closeUpload} onUploaded={async () => { await onReload(); closeUpload(); }} />
      )}

      {moveDoc && (
        <MoveModal doc={moveDoc} onClose={() => setMoveDoc(null)} onMoved={async () => { setMoveDoc(null); await onReload(); }} />
      )}
    </div>
  );
}

function UploadModal({ initialFolder, onClose, onUploaded }) {
  const [folderId, setFolderId] = useState(initialFolder.id);
  const [subcategoryId, setSubcategoryId] = useState(initialFolder.subcategories[0]?.id || "");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [essential, setEssential] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const photoRef = useRef(null);
  const fileRef = useRef(null);
  const folder = FOLDER_BY_ID[folderId];
  const subcategories = folder ? folder.subcategories : [];

  useEffect(() => {
    if (folder && !folder.subcategories.some((s) => s.id === subcategoryId)) {
      setSubcategoryId(folder.subcategories[0]?.id || "");
    }
  }, [folderId, folder, subcategoryId]);

  useEffect(() => {
    if (folder) {
      const sc = folder.subcategories.find((s) => s.id === subcategoryId);
      if (sc && sc.essential) setEssential(true);
    }
  }, [subcategoryId, folder]);

  const onFile = (f) => {
    if (!f) return;
    setError("");
    if (f.size > 10 * 1024 * 1024) { setError("Fichier trop volumineux (max 10 Mo)."); return; }
    setFile(f);
    if (f.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onloadend = () => setPreview(reader.result);
      reader.readAsDataURL(f);
    } else {
      setPreview(null);
    }
  };

  const submit = async () => {
    if (!file) { setError("Veuillez sélectionner ou photographier un document."); return; }
    setUploading(true); setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("folder", folderId);
      fd.append("subcategory", subcategoryId);
      fd.append("essential", essential ? "true" : "false");
      await api.post("/coffre/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded();
    } catch (e) { setError(formatApiError(e)); }
    finally { setUploading(false); }
  };

  return (
    <div data-testid="upload-modal" role="dialog" aria-modal="true" style={{ position: "fixed", inset: 0, zIndex: 9998, background: "rgba(15,20,35,0.6)", backdropFilter: "blur(4px)", display: "flex", alignItems: "flex-end", justifyContent: "center", padding: 0, overflowY: "auto" }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: "var(--auprea-cream)", width: "100%", maxWidth: 620, maxHeight: "94vh", borderRadius: "10px 10px 0 0", boxShadow: "0 -12px 40px rgba(0,0,0,0.35)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ background: "var(--auprea-navy)", color: "var(--auprea-cream)", padding: "18px 22px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", color: "var(--auprea-gold)", marginBottom: 3 }}>Ajouter un document</div>
            <div style={{ fontFamily: "Spectral, serif", fontSize: 20 }}>{folder?.label}</div>
          </div>
          <button onClick={onClose} data-testid="upload-modal-close" style={{ color: "var(--auprea-cream)", opacity: 0.8, padding: 6 }}>
            <X size={22} />
          </button>
        </div>

        <div style={{ padding: 22, overflowY: "auto", flex: 1 }}>
          {!file && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="upload-choice">
                <button onClick={() => photoRef.current?.click()} data-testid="btn-take-photo" style={{ padding: "24px 16px", background: "#fff", border: "1px solid var(--auprea-gold)", display: "flex", flexDirection: "column", alignItems: "center", gap: 10, cursor: "pointer", fontSize: 15, color: "var(--auprea-navy)", fontWeight: 500, minHeight: 130 }}>
                  <Camera size={30} color="#caa86a" />
                  <span>Prendre une photo</span>
                  <span style={{ fontSize: 11, color: "rgba(35,38,46,0.6)", fontWeight: 400 }}>Ouvre l&apos;appareil photo</span>
                </button>
                <button onClick={() => fileRef.current?.click()} data-testid="btn-import-file" style={{ padding: "24px 16px", background: "#fff", border: "1px solid rgba(21,41,79,0.2)", display: "flex", flexDirection: "column", alignItems: "center", gap: 10, cursor: "pointer", fontSize: 15, color: "var(--auprea-navy)", fontWeight: 500, minHeight: 130 }}>
                  <Upload size={30} color="var(--auprea-navy)" />
                  <span>Importer un fichier</span>
                  <span style={{ fontSize: 11, color: "rgba(35,38,46,0.6)", fontWeight: 400 }}>PDF, JPG, PNG, WEBP · max 10 Mo</span>
                </button>
              </div>
              <input ref={photoRef} type="file" accept="image/*" capture="environment" onChange={(e) => onFile(e.target.files?.[0])} style={{ display: "none" }} data-testid="input-photo" />
              <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={(e) => onFile(e.target.files?.[0])} style={{ display: "none" }} data-testid="input-file" />
            </>
          )}

          {file && (
            <div data-testid="upload-step2">
              <div style={{ marginBottom: 16 }}>
                {preview ? (
                  <div style={{ background: "#000", borderRadius: 4, overflow: "hidden", display: "flex", justifyContent: "center", alignItems: "center", maxHeight: 300 }}>
                    <img src={preview} alt="Aperçu" style={{ maxHeight: 300, maxWidth: "100%", objectFit: "contain" }} data-testid="upload-preview-image" />
                  </div>
                ) : (
                  <div style={{ background: "#fff", border: "1px solid rgba(21,41,79,0.12)", padding: 22, display: "flex", alignItems: "center", gap: 14 }} data-testid="upload-preview-file">
                    <FileText size={34} color="#caa86a" />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600, color: "var(--auprea-navy)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{file.name}</div>
                      <div style={{ fontSize: 12, color: "rgba(35,38,46,0.6)" }}>{(file.size / 1024).toFixed(0)} Ko</div>
                    </div>
                  </div>
                )}
                <button onClick={() => { setFile(null); setPreview(null); }} data-testid="btn-retake" style={{ marginTop: 8, fontSize: 13, color: "var(--auprea-navy)", padding: "4px 0", background: "none" }}>
                  ← Reprendre
                </button>
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--auprea-navy)", marginBottom: 5, letterSpacing: "0.03em", textTransform: "uppercase" }}>Dossier</label>
                <select value={folderId} onChange={(e) => setFolderId(e.target.value)} data-testid="select-folder" style={{ width: "100%", padding: "10px 12px", fontSize: 15, background: "#fff", border: "1px solid rgba(21,41,79,0.2)" }}>
                  {COFFRE_FOLDERS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
                </select>
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--auprea-navy)", marginBottom: 5, letterSpacing: "0.03em", textTransform: "uppercase" }}>Type de document</label>
                <select value={subcategoryId} onChange={(e) => setSubcategoryId(e.target.value)} data-testid="select-subcategory" style={{ width: "100%", padding: "10px 12px", fontSize: 15, background: "#fff", border: "1px solid rgba(21,41,79,0.2)" }}>
                  {subcategories.map((sc) => <option key={sc.id} value={sc.id}>{sc.label}{sc.essential ? " ★" : ""}</option>)}
                </select>
              </div>

              <label style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", background: essential ? "rgba(202,168,106,0.15)" : "rgba(21,41,79,0.03)", border: `1px solid ${essential ? "var(--auprea-gold)" : "rgba(21,41,79,0.1)"}`, cursor: "pointer", fontSize: 14 }} data-testid="essential-toggle">
                <input type="checkbox" checked={essential} onChange={(e) => setEssential(e.target.checked)} data-testid="check-essential" />
                <Star size={15} fill={essential ? "#caa86a" : "none"} color={essential ? "#caa86a" : "rgba(35,38,46,0.5)"} />
                <span style={{ color: "var(--auprea-navy)" }}>
                  Marquer comme <strong>essentiel</strong>
                  <span style={{ fontSize: 12, color: "rgba(35,38,46,0.65)", display: "block", marginTop: 2 }}>Document dont votre famille aura besoin en urgence.</span>
                </span>
              </label>

              {error && <div style={{ marginTop: 12, padding: 10, background: "#fdecea", border: "1px solid #ea8b7c", color: "#7a1e0d", fontSize: 13 }} data-testid="upload-error">{error}</div>}
            </div>
          )}
        </div>

        <div style={{ borderTop: "1px solid rgba(21,41,79,0.1)", padding: "12px 22px", background: "#fff", display: "flex", justifyContent: "flex-end", gap: 10, flexWrap: "wrap" }}>
          <button onClick={onClose} disabled={uploading} data-testid="btn-cancel-upload" style={{ padding: "10px 18px", background: "transparent", color: "rgba(35,38,46,0.7)", border: "1px solid rgba(35,38,46,0.2)", fontSize: 14 }}>Annuler</button>
          {file && (
            <button onClick={submit} disabled={uploading || !subcategoryId} className="auprea-btn" data-testid="btn-submit-upload" style={{ padding: "10px 22px", display: "inline-flex", alignItems: "center", gap: 6, fontSize: 14, opacity: uploading || !subcategoryId ? 0.6 : 1 }}>
              {uploading ? "Envoi…" : (<><Upload size={15} /> Déposer dans le coffre</>)}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function MoveModal({ doc, onClose, onMoved }) {
  const [folderId, setFolderId] = useState(doc.folder || "non_classes");
  const [subcategoryId, setSubcategoryId] = useState(doc.subcategory || "autre");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const folder = FOLDER_BY_ID[folderId];

  useEffect(() => {
    if (folder && !folder.subcategories.some((s) => s.id === subcategoryId)) {
      setSubcategoryId(folder.subcategories[0]?.id || "");
    }
  }, [folderId, folder, subcategoryId]);

  const submit = async () => {
    setSaving(true); setError("");
    try {
      await api.patch(`/coffre/${doc.id}`, { folder: folderId, subcategory: subcategoryId });
      onMoved();
    } catch (e) { setError(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="move-modal" style={{ position: "fixed", inset: 0, zIndex: 9998, background: "rgba(15,20,35,0.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: "var(--auprea-cream)", maxWidth: 480, width: "100%", padding: 22 }}>
        <h3 style={{ fontSize: 18, marginBottom: 4, color: "var(--auprea-navy)" }}>Reclasser le document</h3>
        <p style={{ fontSize: 13, color: "rgba(35,38,46,0.65)", marginBottom: 16 }}>{doc.nom}</p>
        <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--auprea-navy)", marginBottom: 4, textTransform: "uppercase" }}>Dossier</label>
        <select value={folderId} onChange={(e) => setFolderId(e.target.value)} data-testid="move-select-folder" style={{ width: "100%", padding: 10, fontSize: 15, marginBottom: 14, background: "#fff", border: "1px solid rgba(21,41,79,0.2)" }}>
          {COFFRE_FOLDERS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
        </select>
        <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--auprea-navy)", marginBottom: 4, textTransform: "uppercase" }}>Type de document</label>
        <select value={subcategoryId} onChange={(e) => setSubcategoryId(e.target.value)} data-testid="move-select-subcategory" style={{ width: "100%", padding: 10, fontSize: 15, marginBottom: 14, background: "#fff", border: "1px solid rgba(21,41,79,0.2)" }}>
          {(folder?.subcategories || []).map((sc) => <option key={sc.id} value={sc.id}>{sc.label}{sc.essential ? " ★" : ""}</option>)}
        </select>
        {error && <div style={{ padding: 10, background: "#fdecea", border: "1px solid #ea8b7c", color: "#7a1e0d", fontSize: 13, marginBottom: 12 }}>{error}</div>}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button onClick={onClose} disabled={saving} data-testid="btn-cancel-move" style={{ padding: "8px 16px", background: "transparent", border: "1px solid rgba(35,38,46,0.2)", fontSize: 14 }}>Annuler</button>
          <button onClick={submit} disabled={saving} className="auprea-btn" data-testid="btn-confirm-move" style={{ padding: "8px 18px", fontSize: 14 }}>{saving ? "…" : "Déplacer"}</button>
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value, testid, icon }) {
  return (
    <div style={{ background: "#fff", border: "1px solid rgba(21,41,79,0.08)", borderLeft: "3px solid var(--auprea-gold)", padding: "12px 14px" }} data-testid={testid}>
      <div style={{ fontSize: 11, color: "rgba(35,38,46,0.6)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4, display: "flex", alignItems: "center", gap: 5 }}>
        {icon}<span>{label}</span>
      </div>
      <div style={{ fontFamily: "Spectral, serif", fontSize: 26, color: "var(--auprea-navy)", fontWeight: 500 }}>{value}</div>
    </div>
  );
}
